import {
  columnToIndex,
  indexToColumn,
  indexToRow,
  rowToIndex,
  splitRange,
  isRangeWithinRange,
  removePartUntilSymbol,
} from "../../../utils/util";
import { ColumnTypes, RowTypes, CellTypes } from "./CellConstants";

const mappings = {
  [`${RowTypes.Activity}_${ColumnTypes.ColumnGroup}`]: CellTypes.ColumnGroup,
  [`${RowTypes.Activity}_${ColumnTypes.Activity}`]: CellTypes.Activity,
  [`${RowTypes.Activity}_${ColumnTypes.TrackType}`]: CellTypes.TrackType,
  [`${RowTypes.Activity}_${ColumnTypes.Player}`]: CellTypes.Content,
  [`${RowTypes.Grid}_${ColumnTypes.Grid}`]: CellTypes.Grid,
  [`${RowTypes.PlusMinus}_${ColumnTypes.Player}`]: CellTypes.PlusMinus,
  [`${RowTypes.Player}_${ColumnTypes.Player}`]: CellTypes.Player,
  [`${RowTypes.PlayerAttribute}_${ColumnTypes.Player}`]: CellTypes.PlayerAttribute,
  [`${RowTypes.RowGroup}_${ColumnTypes.Player}`]: CellTypes.RowGroup,
  [`${RowTypes.Sign}_${ColumnTypes.Player}`]: CellTypes.SignIcon,
  [`${RowTypes.Activity}_${ColumnTypes.Flow}`]: CellTypes.Content,
};

const normalMappings = {
  [`${RowTypes.PlayerAttribute}_${ColumnTypes.Activity}`]: CellTypes.HeaderRightAlign,
  [`${RowTypes.RowGroup}_${ColumnTypes.Activity}`]: CellTypes.HeaderRightAlign,
  [`${RowTypes.Player}_${ColumnTypes.TrackType}`]: CellTypes.HeaderLeftAlign,
  [`${RowTypes.Player}_${ColumnTypes.ColumnGroup}`]: CellTypes.HeaderLeftAlign,
  [`${RowTypes.Player}_${ColumnTypes.Flow}`]: CellTypes.HeaderLeftAlign,
  [`${RowTypes.Player}_${ColumnTypes.Activity}`]: CellTypes.HeaderLeftAlign,
};

const postingMappings = {
  [`${RowTypes.PlayerAttribute}_${ColumnTypes.TrackType}`]: CellTypes.HeaderRightAlign,
  [`${RowTypes.RowGroup}_${ColumnTypes.TrackType}`]: CellTypes.HeaderRightAlign,
  [`${RowTypes.Player}_${ColumnTypes.TrackType}`]: CellTypes.HeaderRightAlign,
  [`${RowTypes.Sign}_${ColumnTypes.TrackType}`]: CellTypes.HeaderLeftAlign,
  [`${RowTypes.Sign}_${ColumnTypes.ColumnGroup}`]: CellTypes.HeaderLeftAlign,
  [`${RowTypes.Sign}_${ColumnTypes.Flow}`]: CellTypes.HeaderLeftAlign,
  [`${RowTypes.Sign}_${ColumnTypes.Activity}`]: CellTypes.HeaderLeftAlign,
};

export const getDefaultCellType = (rowType, columnType, type = null) => {
  if (columnType === ColumnTypes.Number) return CellTypes.Number;
  if (rowType === RowTypes.Letter) return CellTypes.Letter;
  if (rowType === RowTypes.NavHeader || columnType === ColumnTypes.NavHeader) return CellTypes.NavHeader;
  if (rowType === RowTypes.NavProperty || columnType === ColumnTypes.NavProperty) return CellTypes.NavProperty;

  // Merge mappings with type-specific mappings
  const mergedMappings = {
    ...mappings,
    ...(type === "normal" ? normalMappings : {}),
    ...(type === "posting" ? postingMappings : {}),
  };
  return mergedMappings[`${rowType}_${columnType}`] || CellTypes.Header;
};

export const rangeLabelToIndex = (title) => {
  if (!title) return;
  title = title.replace("$", "");
  const range = splitRange(title);

  if (range.length === 2) {
    range[2] = range[0];
    range[3] = range[1];
  }

  return [columnToIndex(range[0]), rowToIndex(range[1]), columnToIndex(range[2]), rowToIndex(range[3])];
};

export const namedRangeLabelToIndex = (title) => {
  title = removePartUntilSymbol(title, "!");
  if (!title) return;
  const range = splitRange(title);

  if (range.length === 2) {
    range[2] = range[0];
    range[3] = range[1];
  }

  return [columnToIndex(range[0]), rowToIndex(range[1]), columnToIndex(range[2]), rowToIndex(range[3])];
};
export const isCellInAnyTableRange = (state, getters, rootState, rootGetters) => (tables, cellRange) => {
  return tables.some((table) => {
    const tableRange = rangeLabelToIndex(table.tableRange);
    return isRangeWithinRange(cellRange, tableRange);
  });
};

// Note in case to combine two single cell ranges,(A10:A10 and B12:B12) it was acting as multiple range A10:A10:B12:B12 converted to A10:B12
export const rangeIndexToLabel = (range) => {
  const startLabel = `${indexToColumn(range[0])}${indexToRow(range[1])}`;
  const endLabel = `${indexToColumn(range[2])}${indexToRow(range[3])}`;

  // If start and end labels are the same, return the startLabel only
  if (startLabel === endLabel) {
    return startLabel;
  }

  // Otherwise, return the full range
  return `${startLabel}:${endLabel}`;
};
export const getCellId = (rowId, columnId) => {
  return `${rowId}_${columnId}`;
};
export const splitCellId = (id) => {
  return id.split("_");
};
export const sortRange = (range) => {
  return [
    Math.min(range[0], range[2]),
    Math.min(range[1], range[3]),
    Math.max(range[0], range[2]),
    Math.max(range[1], range[3]),
  ];
};
export const formatData = (data, contentType) => {
  switch (contentType) {
    case "date":
      return { value: formatDate(data.value) };
    default:
      return data;
  }
};
export const formatDate = (numDate, format = "long") => {
  const MS_PER_DAY = 24 * 60 * 60 * 1000; // Number of milliseconds in a day
  const START_DATE = new Date(1900, 0, 1); // January 1, 1900

  const daysSinceStart = numDate - 1; // Excel starts from 1/1/1900, so subtract 1 day
  const msSinceStart = daysSinceStart * MS_PER_DAY; // Convert days to milliseconds
  const date = new Date(START_DATE.getTime() + msSinceStart); // Add milliseconds to start date

  switch (format) {
    case "short":
      return date.toLocaleDateString("en-US");
    case "long":
      return date.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
    default:
      return date;
  }
};
export const areArraysEqual = (arr1, arr2) => {
  return JSON.stringify(arr1) === JSON.stringify(arr2);
};

export const defaultHotkyesFilterFunction = (event) => {
  // hotkey is effective only when filter return true
  const target = event.target || event.srcElement;
  const { tagName } = target;
  let flag = true;
  // ignore: isContentEditable === 'true', <input> and <textarea> when readOnly state is false, <select>
  if (
    target.isContentEditable ||
    ((tagName === "INPUT" || tagName === "TEXTAREA" || tagName === "SELECT") && !target.readOnly)
  ) {
    flag = false;
  }
  return flag;
};
